import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  SvgIcon,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation, Trans } from 'react-i18next';
import { EXTERNAL_LINKS } from '../../constants/config';

// Custom Discord Icon
const DiscordIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </SvgIcon>
);

// Custom Telegram Icon
const TelegramIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </SvgIcon>
);

interface InfoPopupProps {
  autoShowDelay?: number;
}

const ACCENT = '#dc2626';

// One social entry (Discord / Telegram) — dark glass card with a brand-tinted icon tile.
const SocialCard: React.FC<{ href: string; brand: string; label: string; name: string; icon: React.ReactNode }> = ({ href, brand, label, name, icon }) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.75,
      p: 1.75,
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '14px',
      textDecoration: 'none',
      transition: 'all 0.25s ease',
      '&:hover': {
        background: 'rgba(255,255,255,0.05)',
        borderColor: brand,
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 24px rgba(0,0,0,0.4)`,
      },
    }}
  >
    <Box
      sx={{
        flexShrink: 0,
        width: 44,
        height: 44,
        borderRadius: '11px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `${brand}22`,
        border: `1px solid ${brand}55`,
        color: brand,
        '& svg': { fontSize: 24 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.72rem', lineHeight: 1.3 }}>{label}</Typography>
      <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '1rem', lineHeight: 1.3 }}>{name}</Typography>
    </Box>
  </Box>
);

const InfoPopup: React.FC<InfoPopupProps> = ({ autoShowDelay = 6000 }) => {
  const { t } = useTranslation('home');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (autoShowDelay <= 0) return;

    const timer = setTimeout(() => {
      setIsOpen(true);
    }, autoShowDelay);

    return () => clearTimeout(timer);
  }, [autoShowDelay]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Mark component for highlighted (red-accent) text inside paragraphs.
  const HighlightMark = ({ children }: { children?: React.ReactNode }) => (
    <Box component="span" sx={{ color: ACCENT, fontWeight: 700 }}>
      {children}
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '20px',
          background: 'rgba(16,16,18,0.92)',
          backdropFilter: 'blur(24px)',
          border: `1px solid ${ACCENT}59`,
          boxShadow: `0 30px 90px rgba(0,0,0,0.75), 0 0 60px rgba(220,38,38,0.12)`,
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.82)',
          backdropFilter: 'blur(6px)',
        },
      }}
    >
      {/* faint background art + red glow at the top for depth */}
      <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'url(/img/popup-bg.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.12 }} />
      <Box sx={{ position: 'absolute', top: -120, left: '50%', transform: 'translateX(-50%)', width: 420, height: 260, background: 'radial-gradient(ellipse at center, rgba(220,38,38,0.28), transparent 70%)', pointerEvents: 'none' }} />

      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 10,
          color: 'rgba(255,255,255,0.55)',
          '&:hover': { color: '#fff', background: 'rgba(220,38,38,0.18)' },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ position: 'relative', zIndex: 1, p: { xs: 3, md: 4 } }}>
        {/* eyebrow */}
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Box sx={{ width: 6, height: 6, transform: 'rotate(45deg)', background: ACCENT }} />
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '3px', color: ACCENT, textTransform: 'uppercase' }}>
            Odium World · ЗБТ
          </Typography>
        </Box>

        {/* greeting */}
        <Typography sx={{ fontWeight: 800, mb: 2, color: '#fff', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
          {t('popup.greeting')}
        </Typography>

        {/* body */}
        <Box sx={{ mb: 3 }}>
          {(['intro', 'beta', 'join'] as const).map((key, i) => (
            <Typography
              key={key}
              sx={{ color: 'rgba(255,255,255,0.72)', mb: i < 2 ? 1.5 : 0, lineHeight: 1.7, fontSize: { xs: '0.88rem', md: '0.95rem' } }}
            >
              <Trans i18nKey={`popup.${key}`} t={t} components={{ mark: <HighlightMark /> }} />
            </Typography>
          ))}
        </Box>

        {/* CTA */}
        <Button
          size="large"
          fullWidth
          component="a"
          href={EXTERNAL_LINKS.ZBT_FORM}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClose}
          sx={{
            py: 1.5,
            mb: 2.5,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
            color: '#fff',
            fontWeight: 800,
            fontSize: '1rem',
            textTransform: 'none',
            boxShadow: '0 8px 26px rgba(220,38,38,0.35)',
            transition: 'all 0.25s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: '0 10px 32px rgba(220,38,38,0.5)',
            },
          }}
        >
          <Trans
            i18nKey="popup.button"
            t={t}
            components={{ mark: <Box component="span" sx={{ color: '#fbbf24', fontWeight: 900 }} /> }}
          />
        </Button>

        {/* social */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          <SocialCard href={EXTERNAL_LINKS.DISCORD} brand="#5865F2" label={t('popup.discordText')} name="Discord" icon={<DiscordIcon />} />
          <SocialCard href={EXTERNAL_LINKS.TELEGRAM_CHAT} brand="#0088cc" label={t('popup.telegramText')} name="Telegram" icon={<TelegramIcon />} />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default InfoPopup;
